import React, { useState, useEffect } from 'react';
import { portfolioConfig } from '../config/portfolio';
import { Icon } from './Icons';

const TechStack = () => {
  const [isIntroVisible, setIsIntroVisible] = useState(false);
  const [visibleCategories, setVisibleCategories] = useState([]);

  const techCategories = [
    {
      title: 'Languages',
      items: portfolioConfig.techStack.languages,
      color: 'from-cyan-500 to-blue-500'
    },
    {
      title: 'Frameworks',
      items: portfolioConfig.techStack.frameworks,
      color: 'from-blue-500 to-purple-500'
    },
    {
      title: 'Tools',
      items: portfolioConfig.techStack.tools,
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Strengths',
      items: portfolioConfig.techStack.strengths,
      color: 'from-pink-500 to-red-500'
    },
  ];

  useEffect(() => {
    const section = document.getElementById('skills');
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.id === 'techstack-intro') {
              setIsIntroVisible(true);
            } else if (entry.target.dataset.catIndex !== undefined) {
              const catIndex = Number(entry.target.dataset.catIndex);
              setVisibleCategories(prev => {
                if (!prev.includes(catIndex)) return [...prev, catIndex];
                return prev;
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(document.getElementById('techstack-intro'));
    const categoryEls = document.querySelectorAll('#skills .tech-category');
    categoryEls.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" className="py-20 section-bg-1 relative">
      <div className="max-w-7xl mx-auto px-8">
        {/* Intro */}
        <div
          id="techstack-intro"
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Technical{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Skills
            </span>
          </h2>
        </div>

        {/* Categories */}
        <div className="space-y-12">
          {techCategories.map((category, categoryIndex) => (
            <div
              key={category.title}
              data-cat-index={categoryIndex}
              className={`tech-category relative transition-all duration-700 transform ${
                visibleCategories.includes(categoryIndex)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ animationDelay: `${categoryIndex * 0.2}s` }}
            >
              <div className={`bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-cyan-400/50 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10`}>
                {/* Category Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-3 h-8 bg-gradient-to-b ${category.color} rounded-full`}
                    ></div>
                    <h3
                      className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${category.color}`}
                    >
                      {category.title}
                    </h3>
                  </div>
                </div>

                {/* Tech Pills */}
                <div className="flex flex-wrap gap-3">
                  {category.items.map((item, itemIndex) => (
                    <div
                      key={item.name}
                      className={`group relative inline-flex items-center gap-2 px-4 py-2 bg-gray-700/50 hover:bg-gray-600/50 border-gray-600 hover:border-cyan-400/50 border rounded-full transition-all duration-200 transform hover:scale-105 whitespace-nowrap`}
                      style={{ animationDelay: `${categoryIndex * 0.2 + itemIndex * 0.1}s` }}
                    >
                      <Icon
                        name={item.icon}
                        className={`group-hover:animate-pulse text-cyan-400`}
                      />
                      <span className={`text-sm font-medium text-gray-300 group-hover:text-white`}>
                        {item.name}
                      </span>

                      {item.new && (
                        <div className="relative">
                          <div className="absolute -top-6 -right-5">
                            <Icon
                              name="exclamation"
                              className="text-yellow-400 animate-shake"
                            />
                            <div className="absolute hidden group-hover:flex items-center gap-1 bottom-full left-1/2 -translate-x-3/4 mb-1 px-2 py-1 bg-gray-800 border border-yellow-600 rounded-md whitespace-nowrap shadow-lg z-10">
                              <span className="text-xs text-yellow-300">
                                Currently Learning
                              </span>
                              <div className="absolute top-full left-3/4 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-0 border-t-4 border-yellow-600 border-l-transparent border-r-transparent"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Glow Effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${category.color} rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-300 -z-10`}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
      </div>
    </section>
  );
};

export default TechStack;